import React, { useEffect, useState, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Spinner } from "react-bootstrap";
import {
  getReportMetadata,
  getReportData,
  exportReportCsv,
  downloadFile,
} from "./crud";
import { ReportMetadata, ReportDataRequest, ReportDataResponse } from "./types";
import ReportFilters from "./components/ReportFilters";
import ReportTable from "./components/ReportTable";
import ReportTotals from "./components/ReportTotals";
import ReportChart from "./components/ReportChart";
import ReportExport from "./components/ReportExport";
import { format, subDays, startOfMonth, startOfYear } from "date-fns";
import { trackReportUsage } from "./reportUsageTracker";
import { useAuth } from "../../modules/auth";

// Mapping from numeric reportId to string tracking ID
const DATE_PRESETS: Record<string, { from: Date; to: Date }> = {
  LAST_7_DAYS: { from: subDays(new Date(), 6), to: new Date() },
  LAST_30_DAYS: { from: subDays(new Date(), 29), to: new Date() },
  THIS_MONTH: { from: startOfMonth(new Date()), to: new Date() },
  LAST_MONTH: {
    from: startOfMonth(subDays(startOfMonth(new Date()), 1)),
    to: subDays(startOfMonth(new Date()), 1),
  },
  THIS_YEAR: { from: startOfYear(new Date()), to: new Date() },
};

const formatDateStr = (d: Date) => format(d, "yyyy-MM-dd");

const REPORT_TRACKING_IDS: Record<number, string> = {
  1: "treatments-by-advisor",
  2: "treatments-by-supervisor",
  3: "treatments-by-branch",
  4: "treatments-by-dealer",
  5: "most-used-treatments",
  6: "treatment-category-analysis",
  7: "job-card-summary",
  8: "advisor-performance",
  9: "revenue-analysis",
  10: "vehicle-segment-analysis",
  11: "deleted-treatments",
  12: "daily-job-card-volume",
  13: "water-wash-analysis",
  14: "vehicle-model",
  15: "water-wash-list",
  16: "treatment-billing",
  17: "treatment-monthly-view-dp",
  18: "treatment-monthly-view-cp",
};

interface GenericReportProps {
  reportId: number;
  title?: string;
  toolbarButtons?: React.ReactNode;
  visibleColumns?: string[];
  externalFilters?: Record<string, any>;
}

const GenericReport: React.FC<GenericReportProps> = ({ reportId, title, toolbarButtons, visibleColumns, externalFilters }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { currentUser } = useAuth();
  const [metadata, setMetadata] = useState<ReportMetadata | null>(null);
  const [data, setData] = useState<ReportDataResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isDataLoading, setIsDataLoading] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showDescription, setShowDescription] = useState(false);
  const [showFilters, setShowFilters] = useState(false);

  const [filters, setFilters] = useState<Record<string, any>>({});
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [sortBy, setSortBy] = useState<string | undefined>();
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc");
  const [showChart, setShowChart] = useState(false);
  const [isPrintLoading, setIsPrintLoading] = useState(false);

  // Track report usage on mount
  useEffect(() => {
    const trackingId = REPORT_TRACKING_IDS[reportId];
    if (trackingId) {
      trackReportUsage(trackingId);
    }
  }, [reportId]);

  // Load metadata on mount
  useEffect(() => {
    setIsLoading(true);
    setError(null);
    getReportMetadata(reportId)
      .then(({ data }) => {
        setMetadata(data);
        setSortBy(data.defaultSortField);
        setSortOrder(data.defaultSortOrder);

        // Initialize filters with defaults, resolving date presets to actual dates
        const defaultFilters: Record<string, any> = {};
        data.filters.forEach((filter) => {
          if (filter.defaultValue !== undefined) {
            if (filter.type?.toLowerCase() === "date_range") {
              // defaultValue can be a string "THIS_MONTH" or an object {name: "THIS_MONTH", displayName: "This Month"}
              const presetName =
                typeof filter.defaultValue === "string"
                  ? filter.defaultValue
                  : filter.defaultValue?.name || "THIS_MONTH";
              const preset = DATE_PRESETS[presetName];
              if (preset) {
                defaultFilters[filter.field] = {
                  from: formatDateStr(preset.from),
                  to: formatDateStr(preset.to),
                  preset: presetName,
                };
              } else {
                defaultFilters[filter.field] = {
                  from: formatDateStr(startOfMonth(new Date())),
                  to: formatDateStr(new Date()),
                  preset: "THIS_MONTH",
                };
              }
            } else {
              defaultFilters[filter.field] = filter.defaultValue;
            }
          }
        });
        setFilters(defaultFilters);
        setIsLoading(false);
      })
      .catch((err) => {
        console.error("Error loading report metadata:", err);
        setError("Failed to load report configuration. Please try again.");
        setIsLoading(false);
      });
  }, [reportId]);

  // Load data when filters/pagination/sorting change
  const loadData = useCallback(() => {
    if (!metadata) return;

    setIsDataLoading(true);
    setError(null);
    const mergedFilters = externalFilters ? { ...filters, ...externalFilters } : filters;
    const request: ReportDataRequest = {
      filters: mergedFilters,
      page,
      pageSize,
      sortBy,
      sortOrder,
      includeChartData: showChart && metadata.chartConfig.enabled,
    };

    getReportData(reportId, request)
      .then(({ data }) => {
        setData(data);
        setIsDataLoading(false);
      })
      .catch((err) => {
        console.error("Error loading report data:", err);
        setError("Failed to load report data. Please check your filters and try again.");
        setIsDataLoading(false);
      });
  }, [reportId, metadata, filters, externalFilters, page, pageSize, sortBy, sortOrder, showChart]);

  useEffect(() => {
    if (metadata) {
      loadData();
    }
  }, [metadata, filters, externalFilters, page, pageSize, sortBy, sortOrder]);

  // Handle filter changes
  const handleFilterChange = (newFilters: Record<string, any>) => {
    setFilters(newFilters);
    setPage(1); // Reset to first page on filter change
  };

  // Handle sort change
  const handleSortChange = (field: string) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortBy(field);
      setSortOrder("desc");
    }
  };

  // Handle pagination
  const handlePageChange = (newPage: number) => {
    setPage(newPage);
  };

  const handlePageSizeChange = (newSize: number) => {
    setPageSize(newSize);
    setPage(1);
  };

  // Export handlers
  const handleExportCsv = async () => {
    if (!metadata) return;

    setIsExporting(true);
    const mergedFilters = externalFilters ? { ...filters, ...externalFilters } : filters;

    try {
      if (visibleColumns) {
        // Client-side CSV generation with only selected columns
        const request: ReportDataRequest = {
          filters: mergedFilters,
          page: 1,
          pageSize: 999999,
          sortBy,
          sortOrder,
        };
        const { data: allData } = await getReportData(reportId, request);
        const cols = metadata.columns.filter(c => visibleColumns.includes(c.field));

        const csvRows: string[] = [];
        csvRows.push(cols.map(c => c.displayName).join(","));
        for (const row of allData.data) {
          csvRows.push(cols.map(col => {
            const val = row[col.field];
            if (val === null || val === undefined) return "";
            let str = String(val);
            if (str.includes(",") || str.includes("\"") || str.includes("\n")) {
              str = "\"" + str.replace(/"/g, "\"\"") + "\"";
            }
            return str;
          }).join(","));
        }

        if (metadata.showTotals && allData.totals) {
          csvRows.push(cols.map((col, idx) => {
            if (idx === 0) return "TOTALS";
            if (col.includeInTotals && allData.totals![col.field] !== undefined) {
              return String(allData.totals![col.field]);
            }
            return "";
          }).join(","));
        }

        const csvContent = csvRows.join("\n");
        const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
        const filename = `${metadata.name.replace(/\s+/g, "_")}_${format(new Date(), "yyyy-MM-dd")}.csv`;
        downloadFile(blob, filename);
      } else {
        const request: ReportDataRequest = {
          filters: mergedFilters,
          page: 1,
          pageSize: 999999,
          sortBy,
          sortOrder,
        };
        const response = await exportReportCsv(reportId, request);
        const filename = `${metadata.name.replace(/\s+/g, "_")}_${format(new Date(), "yyyy-MM-dd")}.csv`;
        downloadFile(response.data, filename);
      }
    } catch (err) {
      console.error("Export failed:", err);
      alert("Failed to export report. Please try again.");
    } finally {
      setIsExporting(false);
    }
  };

  const formatPrintValue = (value: any, colType: string | undefined): string => {
    if (value === null || value === undefined) return "-";
    const t = colType?.toLowerCase();
    switch (t) {
      case "currency":
        return new Intl.NumberFormat("en-IN", {
          style: "currency", currency: "INR", minimumFractionDigits: 2,
        }).format(Number(value));
      case "number":
        return new Intl.NumberFormat("en-IN").format(Number(value));
      case "percentage":
        return `${Number(value).toFixed(2)}%`;
      case "boolean":
        return value ? "Yes" : "No";
      default:
        return String(value);
    }
  };

  const handlePrint = async () => {
    if (!metadata) return;

    setIsPrintLoading(true);
    try {
      const mergedFilters = externalFilters ? { ...filters, ...externalFilters } : filters;
      const request: ReportDataRequest = {
        filters: mergedFilters,
        page: 1,
        pageSize: 999999,
        sortBy,
        sortOrder,
      };
      const { data: allData } = await getReportData(reportId, request);
      const visibleCols = visibleColumns
        ? metadata.columns.filter((col) => visibleColumns.includes(col.field))
        : metadata.columns.filter((col) => col.visible);

      const isNumericType = (t?: string) =>
        ["number", "currency", "percentage"].includes(t?.toLowerCase() || "");

      // Build table header
      const headerCells = visibleCols
        .map((col) => `<th style="text-align:${isNumericType(col.type) ? "right" : "left"}">${col.displayName}</th>`)
        .join("");

      // Build table rows
      const rows = allData.data
        .map(
          (row, i) =>
            `<tr class="${i % 2 === 0 ? "even" : "odd"}">${visibleCols
              .map(
                (col) =>
                  `<td style="text-align:${isNumericType(col.type) ? "right" : "left"}">${formatPrintValue(row[col.field], col.type)}</td>`
              )
              .join("")}</tr>`
        )
        .join("");

      // Build totals row
      let totalsRow = "";
      if (metadata.showTotals && allData.totals && Object.keys(allData.totals).length > 0) {
        const totalsCells = visibleCols
          .map((col, idx) => {
            if (idx === 0) return `<td class="totals-cell">TOTALS</td>`;
            const val = col.includeInTotals ? allData.totals![col.field] : null;
            const formatted = val !== null && val !== undefined ? formatPrintValue(val, col.type) : "";
            return `<td class="totals-cell" style="text-align:${isNumericType(col.type) ? "right" : "left"}">${formatted}</td>`;
          })
          .join("");
        totalsRow = `<tr>${totalsCells}</tr>`;
      }

      // Date range string
      let dateRangeStr = "";
      const dateRange = filters?.dateRange;
      if (dateRange && typeof dateRange === "object" && dateRange.from && dateRange.to) {
        try {
          const fromStr = format(new Date(dateRange.from), "dd-MMM-yyyy");
          const toStr = format(new Date(dateRange.to), "dd-MMM-yyyy");
          dateRangeStr = `${fromStr}  to  ${toStr}`;
        } catch {
          dateRangeStr = `${dateRange.from}  to  ${dateRange.to}`;
        }
      }

      const generatedAt = format(new Date(), "dd-MMM-yyyy hh:mm a");
      const reportTitle = title || metadata.name;
      const orgName = currentUser?.orgName || "";
      const orgDetails = [currentUser?.orgAddress, currentUser?.orgCity, currentUser?.orgPinCode]
        .filter(Boolean)
        .join(", ");
      const orgPhone = currentUser?.orgPhone ? `Tel: ${currentUser.orgPhone}` : "";

      const html = `<!DOCTYPE html>
<html>
<head>
  <title>${reportTitle}</title>
  <style>
    * { margin: 0; padding: 0; box-sizing: border-box; }
    body { font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif; padding: 20px; color: #333; }

    .header-table { width: 100%; border-collapse: collapse; margin-bottom: 4px; }
    .org-name { font-size: 18px; font-weight: 700; color: #1a1a2e; letter-spacing: 0.5px; }
    .org-details { font-size: 10px; color: #666; margin-top: 2px; }
    .generated { font-size: 10px; color: #888; text-align: right; }
    .divider { border-bottom: 2px solid #1a1a2e; margin: 8px 0 12px 0; }
    .report-title { font-size: 16px; font-weight: 700; color: #1a1a2e; margin-bottom: 2px; }
    .report-desc { font-size: 11px; color: #666; margin-bottom: 8px; }
    .meta-table { width: 100%; border-collapse: collapse; margin-bottom: 12px; }
    .meta-table td { font-size: 11px; color: #444; }

    table.data-table { width: 100%; border-collapse: collapse; font-size: 9px; margin-top: 4px; }
    table.data-table thead tr { background-color: #f0f0f0; }
    table.data-table thead th {
      padding: 5px 6px; font-size: 8px; font-weight: 700;
      text-transform: uppercase; letter-spacing: 0.3px;
      border-bottom: 2px solid #1a1a2e; color: #333;
    }
    table.data-table tbody tr { page-break-inside: avoid; }
    table.data-table tbody td { padding: 4px 6px; font-size: 9px; border-bottom: 0.5px solid #ddd; color: #333; }
    table.data-table tbody tr.even { background-color: #fafafa; }
    table.data-table tbody tr.odd { background-color: #fff; }

    .totals-cell {
      padding: 6px 8px; font-weight: 700;
      border-top: 2px solid #1a1a2e; border-bottom: 2px solid #1a1a2e;
      background-color: #f0f0f0; font-size: 9px;
    }

    .footer {
      border-top: 1px solid #ddd; padding-top: 6px; margin-top: 20px;
      display: flex; justify-content: space-between; font-size: 9px; color: #999;
    }

    @media print {
      @page { size: A4 landscape; margin: 12mm 10mm; }
      body { padding: 0; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
      table.data-table thead { display: table-header-group; }
    }
  </style>
</head>
<body>
  <table class="header-table">
    <tr>
      <td>
        <div class="org-name">${orgName}</div>
        ${orgDetails || orgPhone ? `<div class="org-details">${orgDetails}${orgPhone ? `<span style="margin-left:10px">${orgPhone}</span>` : ""}</div>` : ""}
      </td>
      <td class="generated">Generated: ${generatedAt}</td>
    </tr>
  </table>

  <div class="divider"></div>

  <div class="report-title">${reportTitle}</div>
  <div class="report-desc">${metadata.description}</div>

  <table class="meta-table">
    <tr>
      ${dateRangeStr ? `<td><strong>Period:</strong> ${dateRangeStr}</td>` : ""}
      <td style="text-align:${dateRangeStr ? "right" : "left"}"><strong>Total Records:</strong> ${allData.totalCount}</td>
    </tr>
  </table>

  <table class="data-table">
    <thead><tr>${headerCells}</tr></thead>
    <tbody>${rows}</tbody>
    ${totalsRow ? `<tfoot>${totalsRow}</tfoot>` : ""}
  </table>

  <div class="footer">
    <span>${reportTitle} | ${orgName}</span>
    <span>Printed: ${generatedAt}</span>
  </div>

  <script>window.onload = function() { window.print(); };<\/script>
</body>
</html>`;

      const printWindow = window.open("", "_blank");
      if (printWindow) {
        printWindow.document.write(html);
        printWindow.document.close();
      }
    } catch (err) {
      console.error("Failed to load all data for print:", err);
      alert("Failed to prepare print data. Please try again.");
    } finally {
      setIsPrintLoading(false);
    }
  };

  // Smart back navigation - go to previous page if available, otherwise default to All Reports
  const handleBackNavigation = () => {
    // Check if we have a "from" state passed during navigation
    const fromPath = (location.state as any)?.from;
    if (fromPath) {
      navigate(fromPath);
      return;
    }

    // Check if there's browser history we can go back to
    if (window.history.length > 2) {
      navigate(-1);
    } else {
      // Default to All Reports if no history
      navigate("/reports");
    }
  };

  // Determine back button label based on where user came from
  const getBackButtonLabel = () => {
    const fromPath = (location.state as any)?.from;
    if (fromPath === "/" || fromPath === "/dashboard") {
      return "Dashboard";
    }
    if (fromPath?.includes("/reports")) {
      return "All Reports";
    }
    if (window.history.length > 2) {
      return "Back";
    }
    return "All Reports";
  };

  // Check if we have only a single date_range filter - show it inline in the toolbar
  const isSingleDateFilter =
    metadata &&
    metadata.filters.length === 1 &&
    metadata.filters[0].type?.toLowerCase() === "date_range";

  const singleDateFilter = isSingleDateFilter && metadata ? metadata.filters[0] : null;

  // Get current date range value for inline picker
  const getInlineDateRange = () => {
    if (!singleDateFilter) return null;
    const dateValue = filters[singleDateFilter.field] || {};
    const today = new Date();
    const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
    const endOfMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0);
    return {
      from: dateValue.from ? new Date(dateValue.from) : startOfMonth,
      to: dateValue.to ? new Date(dateValue.to) : endOfMonth,
      preset: dateValue.preset || "thisMonth",
    };
  };

  // Handle inline date range change
  const handleInlineDateRangeChange = (from: string, to: string) => {
    if (!singleDateFilter) return;
    const newFilters = {
      ...filters,
      [singleDateFilter.field]: {
        from,
        to,
      },
    };
    setFilters(newFilters);
    setPage(1);
  };

  // Loading state
  if (isLoading) {
    return (
      <div
        className="d-flex justify-content-center align-items-center"
        style={{ height: 400 }}
      >
        <Spinner animation="border" variant="primary" />
        <span className="ms-3 text-muted">Loading report configuration...</span>
      </div>
    );
  }

  // Error state
  if (error && !metadata) {
    return (
      <div className="alert alert-danger d-flex align-items-center" role="alert">
        <i className="bi bi-exclamation-triangle-fill me-2"></i>
        {error}
        <button
          className="btn btn-sm btn-danger ms-auto"
          onClick={() => window.location.reload()}
        >
          Retry
        </button>
      </div>
    );
  }

  if (!metadata) return null;

  const inlineDateRange = getInlineDateRange();

  return (
    <div>
      <div className="card">
        {/* Header */}
        <div className="card-header border-0 pt-6 d-print-none">
          <div className="card-title">
            <h3 className="card-title align-items-start flex-column">
              <span className="card-label fw-bold fs-3 mb-1">
                {title || metadata.name}
                {metadata.detailedDescription && (
                  <button
                    className="btn btn-icon btn-sm btn-light-primary ms-2"
                    onClick={() => setShowDescription(!showDescription)}
                    title={showDescription ? "Hide details" : "Show details"}
                    style={{ verticalAlign: "middle" }}
                  >
                    <i className={`bi ${showDescription ? "bi-info-circle-fill" : "bi-info-circle"} fs-5`}></i>
                  </button>
                )}
              </span>
              <span className="text-muted mt-1 fw-semibold fs-7">
                {metadata.description}
              </span>
            </h3>
          </div>
          <div className="card-toolbar">
            {toolbarButtons}
            <button
              className="btn btn-sm btn-light-primary me-3"
              onClick={handleBackNavigation}
              title={`Back to ${getBackButtonLabel()}`}
            >
              <i className="bi bi-arrow-left fs-5 me-1"></i>
              {getBackButtonLabel()}
            </button>
            {/* Inline Date Range Picker for single date filter */}
            {isSingleDateFilter && inlineDateRange && (
              <div className="me-3 d-flex align-items-center gap-2">
                <input
                  type="date"
                  className="form-control form-control-sm form-control-solid"
                  value={inlineDateRange.from instanceof Date ? format(inlineDateRange.from, "yyyy-MM-dd") : ""}
                  onChange={(e) =>
                    handleInlineDateRangeChange(
                      e.target.value,
                      inlineDateRange.to instanceof Date ? format(inlineDateRange.to, "yyyy-MM-dd") : ""
                    )
                  }
                />
                <span className="text-muted">to</span>
                <input
                  type="date"
                  className="form-control form-control-sm form-control-solid"
                  value={inlineDateRange.to instanceof Date ? format(inlineDateRange.to, "yyyy-MM-dd") : ""}
                  onChange={(e) =>
                    handleInlineDateRangeChange(
                      inlineDateRange.from instanceof Date ? format(inlineDateRange.from, "yyyy-MM-dd") : "",
                      e.target.value
                    )
                  }
                />
              </div>
            )}
            <ReportExport
              onExportCsv={handleExportCsv}
              onPrint={handlePrint}
              onRefresh={loadData}
              showChart={showChart}
              onToggleChart={() => setShowChart(!showChart)}
              chartEnabled={metadata.chartConfig.enabled}
              isExporting={isExporting}
              isPrintLoading={isPrintLoading}
            />
          </div>
        </div>

        {/* Detailed Description */}
        {showDescription && metadata.detailedDescription && (
          <div className="card-body pt-0 pb-4 d-print-none">
            <div
              className="notice d-flex rounded border border-primary border-dashed p-6"
              style={{ backgroundColor: "rgba(0, 158, 247, 0.05)" }}
            >
              <i className="bi bi-lightbulb text-primary fs-2x me-4"></i>
              <div className="d-flex flex-stack flex-grow-1">
                <div className="fw-semibold">
                  <h4 className="text-gray-900 fw-bold mb-2">About this Report</h4>
                  <div className="fs-6 text-gray-700" style={{ lineHeight: "1.8" }}>
                    {metadata.detailedDescription}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Filters - hide when single date filter (shown inline in toolbar) */}
        {metadata.filters && metadata.filters.length > 0 && !isSingleDateFilter && (
          <div className="card-body pt-0 d-print-none">
            <div
              className="d-flex align-items-center justify-content-between cursor-pointer py-3 px-4 rounded"
              onClick={() => setShowFilters(!showFilters)}
              style={{
                backgroundColor: showFilters ? "rgba(0, 158, 247, 0.08)" : "var(--bs-gray-100)",
                border: showFilters ? "1px solid rgba(0, 158, 247, 0.3)" : "1px solid var(--bs-gray-300)",
                transition: "all 0.2s ease",
              }}
            >
              <div className="d-flex align-items-center">
                <i className={`bi bi-funnel${showFilters ? "-fill text-primary" : " text-gray-600"} fs-4 me-3`}></i>
                <div>
                  <span className={`fw-bold ${showFilters ? "text-primary" : "text-gray-800"}`}>
                    Filters
                  </span>
                  <span className="text-muted ms-2 fs-7">
                    ({metadata.filters.length} {metadata.filters.length === 1 ? "filter" : "filters"} available)
                  </span>
                </div>
              </div>
              <div className="d-flex align-items-center">
                {!showFilters && Object.keys(filters).length > 0 && (
                  <span className="badge badge-primary me-3">
                    {Object.keys(filters).length} applied
                  </span>
                )}
                <i className={`bi bi-chevron-${showFilters ? "up" : "down"} text-gray-600`}></i>
              </div>
            </div>
            {showFilters && (
              <div className="mt-4">
                <ReportFilters
                  filters={metadata.filters}
                  values={filters}
                  onChange={handleFilterChange}
                  onApply={loadData}
                />
              </div>
            )}
          </div>
        )}

        {/* Error message */}
        {error && (
          <div className="card-body pt-0">
            <div className="alert alert-warning d-flex align-items-center mb-0">
              <i className="bi bi-exclamation-triangle me-2"></i>
              {error}
            </div>
          </div>
        )}

        {/* Chart */}
        {showChart && metadata.chartConfig.enabled && data && data.data.length > 0 && (
          <div className="card-body pt-0">
            <ReportChart config={metadata.chartConfig} data={data.data} />
          </div>
        )}

        {/* Table */}
        <div className="card-body py-4">
          <ReportTable
            columns={visibleColumns ? metadata.columns.filter(c => visibleColumns.includes(c.field)).map(c => ({...c, visible: true})) : metadata.columns}
            data={data?.data || []}
            isLoading={isDataLoading}
            sortBy={sortBy}
            sortOrder={sortOrder}
            onSortChange={handleSortChange}
            page={page}
            pageSize={pageSize}
            totalCount={data?.totalCount || 0}
            onPageChange={handlePageChange}
            onPageSizeChange={handlePageSizeChange}
          />
        </div>

        {/* Totals (screen only - card layout) */}
        {metadata.showTotals && data?.totals && Object.keys(data.totals).length > 0 && (
          <div className="card-body pt-0 d-print-none">
            <ReportTotals columns={visibleColumns ? metadata.columns.filter(c => visibleColumns.includes(c.field)).map(c => ({...c, visible: true})) : metadata.columns} totals={data.totals} />
          </div>
        )}
      </div>

      <style>{`
        .cursor-pointer {
          cursor: pointer;
        }
      `}</style>
    </div>
  );
};

export default GenericReport;
